import { useEffect, useState } from 'react';
import { supabase } from './supabaseClient';

const GOLD = '#C9A84C';
const GOLD_LIGHT = '#E8C96A';
const GOLD_PALE = '#F9F1DC';
const GOLD_BORDER = 'rgba(201,168,76,0.2)';
const CREAM = '#FDFBF7';
const CHARCOAL = '#1C1C1E';
const MUTED = '#8A8070';
const WHITE = '#FFFFFF';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600;700&family=Jost:wght@300;400;500;600&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Jost', sans-serif; background: ${CREAM}; }
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-thumb { background: ${GOLD_BORDER}; border-radius: 4px; }
  .tab-btn:hover { color: ${GOLD} !important; }
  .pageant-card { transition: all 0.2s; }
  .pageant-card:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(201,168,76,0.15) !important; cursor: pointer; }
  .profile-input { width: 100%; padding: 12px 14px; border: 1px solid ${GOLD_BORDER}; border-radius: 10px; background: ${WHITE}; font-family: 'Jost', sans-serif; font-size: 14px; color: ${CHARCOAL}; outline: none; transition: border 0.2s; }
  .profile-input:focus { border-color: ${GOLD}; box-shadow: 0 0 0 3px rgba(201,168,76,0.1); }
  .profile-input::placeholder { color: #C4B8A8; }
`;

function GoldButton({ children, onClick, outline, style, disabled, small }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      padding: small ? '7px 14px' : '10px 22px',
      background: outline ? 'transparent' : `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})`,
      border: `1px solid ${outline ? GOLD_BORDER : 'transparent'}`,
      borderRadius: '8px', fontSize: small ? '12px' : '13px',
      fontFamily: "'Jost', sans-serif", fontWeight: '600',
      color: outline ? GOLD : WHITE, cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1, letterSpacing: '0.04em',
      transition: 'all 0.2s', ...style,
    }}>{children}</button>
  );
}

function ScoreReveal({ profile, pageant }) {
  const [tabulation, setTabulation] = useState(null);
  const [scores, setScores] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => { fetchScores(); }, [profile.id]);

  async function fetchScores() {
    setLoading(true);
    const [tabRes, catRes] = await Promise.all([
      supabase.from('tabulations').select('*').eq('contestant_profile_id', profile.id).single(),
      supabase.from('categories').select('*').eq('pageant_id', pageant.id).order('display_order'),
    ]);
    setTabulation(tabRes.data);
    setCategories(catRes.data || []);

    if (tabRes.data) {
      // Get scores with anonymous feedback
      const { data: scoresData } = await supabase
        .from('scores')
        .select('raw_score, feedback_text, category_id')
        .eq('contestant_profile_id', profile.id);
      setScores(scoresData || []);
    }
    setLoading(false);
  }

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: MUTED }}>Loading scores...</div>;

  if (!tabulation) {
    return (
      <div style={{ background: WHITE, border: `1px solid ${GOLD_BORDER}`, borderRadius: '14px', padding: '32px', textAlign: 'center' }}>
        <div style={{ fontSize: '36px', marginBottom: '12px' }}>⏳</div>
        <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '20px', color: CHARCOAL, marginBottom: '8px' }}>Results Not Yet Released</h3>
        <p style={{ fontSize: '13px', color: MUTED, lineHeight: '1.7' }}>Your director hasn't released results yet. Check back after the pageant concludes.</p>
      </div>
    );
  }

  const rankEmoji = tabulation.rank === 1 ? '🥇' : tabulation.rank === 2 ? '🥈' : tabulation.rank === 3 ? '🥉' : `#${tabulation.rank}`;

  return (
    <div>
      {/* Score reveal card */}
      {!revealed ? (
        <div style={{ background: CHARCOAL, borderRadius: '16px', padding: '40px', textAlign: 'center', marginBottom: '20px', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '300px', height: '300px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(201,168,76,0.15), transparent 70%)', pointerEvents: 'none' }} />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>♛</div>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '28px', color: WHITE, marginBottom: '8px' }}>Your results are ready</h2>
            <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.4)', marginBottom: '28px' }}>{pageant.name}</p>
            <GoldButton onClick={() => setRevealed(true)} style={{ fontSize: '15px', padding: '14px 32px' }}>Reveal My Scores ✨</GoldButton>
          </div>
        </div>
      ) : (
        <div>
          {/* Final score */}
          <div style={{ background: CHARCOAL, borderRadius: '16px', padding: '32px', textAlign: 'center', marginBottom: '20px', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '300px', height: '300px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(201,168,76,0.15), transparent 70%)', pointerEvents: 'none' }} />
            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '8px' }}>{pageant.name}</div>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '80px', color: GOLD, fontWeight: '700', lineHeight: 1, marginBottom: '4px' }}>{tabulation.weighted_total?.toFixed(2)}</div>
              <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.4)', marginBottom: '16px' }}>Final Score</div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: '100px', padding: '8px 20px' }}>
                <span style={{ fontSize: '20px' }}>{rankEmoji}</span>
                <span style={{ fontSize: '15px', color: GOLD, fontWeight: '600' }}>
                  {tabulation.rank === 1 ? '1st Place' : tabulation.rank === 2 ? '2nd Place' : tabulation.rank === 3 ? '3rd Place' : `Ranked #${tabulation.rank}`}
                </span>
              </div>
            </div>
          </div>

          {/* Category breakdown */}
          <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '20px', color: CHARCOAL, marginBottom: '14px' }}>Score Breakdown</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '28px' }}>
            {categories.map(cat => {
              const catScores = scores.filter(s => s.category_id === cat.id);
              const avg = catScores.length > 0 ? (catScores.reduce((sum, s) => sum + s.raw_score, 0) / catScores.length).toFixed(2) : '—';
              const pct = avg !== '—' ? Math.round((avg / cat.max_score) * 100) : 0;
              return (
                <div key={cat.id} style={{ background: WHITE, border: `1px solid ${GOLD_BORDER}`, borderRadius: '12px', padding: '16px 20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: CHARCOAL }}>{cat.name}</div>
                      <div style={{ fontSize: '11px', color: MUTED }}>{cat.weight_percent}% of total score</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '28px', color: GOLD, fontWeight: '700', lineHeight: 1 }}>{avg}</div>
                      <div style={{ fontSize: '11px', color: MUTED }}>out of {cat.max_score}</div>
                    </div>
                  </div>
                  <div style={{ height: '6px', background: '#F0EBE0', borderRadius: '3px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: `linear-gradient(90deg, ${GOLD}, ${GOLD_LIGHT})`, borderRadius: '3px', transition: 'width 1s ease' }} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Anonymous judge feedback */}
          {scores.some(s => s.feedback_text) && (
            <div>
              <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '20px', color: CHARCOAL, marginBottom: '8px' }}>Judge Feedback</h3>
              <p style={{ fontSize: '12px', color: MUTED, marginBottom: '14px' }}>Written feedback from your judges — shared anonymously to help you grow.</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {categories.map(cat => {
                  const catFeedback = scores.filter(s => s.category_id === cat.id && s.feedback_text);
                  if (catFeedback.length === 0) return null;
                  return (
                    <div key={cat.id} style={{ background: WHITE, border: `1px solid ${GOLD_BORDER}`, borderRadius: '12px', padding: '16px 20px' }}>
                      <div style={{ fontSize: '11px', color: GOLD, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '10px', fontWeight: '600' }}>{cat.name}</div>
                      {catFeedback.map((s, i) => (
                        <div key={i} style={{ padding: '10px 0', borderTop: i > 0 ? `1px solid ${GOLD_BORDER}` : 'none' }}>
                          <div style={{ fontSize: '13px', color: CHARCOAL, lineHeight: '1.7', fontStyle: 'italic' }}>"{s.feedback_text}"</div>
                          <div style={{ fontSize: '11px', color: MUTED, marginTop: '4px' }}>— Anonymous Judge · Score: {s.raw_score}/{cat.max_score}</div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ContestantDashboard({ user, onSignOut, onSwitchRole }) {
  const [activeTab, setActiveTab] = useState('history');
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [bio, setBio] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => { fetchData(); }, []);

  async function fetchData() {
    setLoading(true);
    const [userRes, profileRes] = await Promise.all([
      supabase.from('users').select('*').eq('id', user.id).single(),
      supabase.from('contestant_profiles')
        .select('*, pageants(id, name, event_date, status, organizations(name))')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false }),
    ]);
    if (userRes.data) {
      setUserData(userRes.data);
      setFullName(userRes.data.full_name || '');
      setPhone(userRes.data.phone || '');
      setBio(userRes.data.bio || '');
    }
    setProfiles(profileRes.data || []);
    setLoading(false);
  }

  async function saveProfile() {
    setSaving(true);
    await supabase.from('users').update({ full_name: fullName, phone, bio }).eq('id', user.id);
    setSaving(false); setSaved(true);
    setTimeout(() => { setSaved(false); setEditMode(false); }, 1500);
    fetchData();
  }

  if (loading) {
    return (
      <>
        <style>{styles}</style>
        <div style={{ minHeight: '100vh', background: CREAM, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ width: '52px', height: '52px', background: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})`, borderRadius: '13px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', marginBottom: '12px' }}>♛</div>
            <p style={{ color: GOLD, fontSize: '12px', letterSpacing: '0.15em', textTransform: 'uppercase' }}>Loading...</p>
          </div>
        </div>
      </>
    );
  }

  const tabs = [
    { id: 'history', label: '🌟 My Pageants', count: profiles.length },
    { id: 'profile', label: '👤 My Profile' },
  ];

  return (
    <>
      <style>{styles}</style>
      <div style={{ minHeight: '100vh', background: CREAM }}>

        {/* Header */}
        <div style={{ background: CHARCOAL, padding: '0 24px' }}>
          <div style={{ maxWidth: '680px', margin: '0 auto', padding: '20px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: '36px', height: '36px', background: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})`, borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>♛</div>
              <div>
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '18px', color: WHITE, fontWeight: '600', lineHeight: 1 }}>Scorenity</div>
                <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.35)', letterSpacing: '0.08em' }}>Contestant Portal</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.35)', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{userData?.full_name || user.email}</span>
              {onSwitchRole && <button onClick={onSwitchRole} style={{ background: 'none', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '6px', padding: '5px 10px', fontSize: '11px', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', fontFamily: "'Jost', sans-serif" }}>Switch</button>}
              <button onClick={onSignOut} style={{ background: 'none', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '6px', padding: '5px 10px', fontSize: '11px', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', fontFamily: "'Jost', sans-serif" }}>Sign Out</button>
            </div>
          </div>
        </div>

        <div style={{ maxWidth: '680px', margin: '0 auto', padding: '28px 24px' }}>

          {/* Welcome card */}
          <div style={{ background: WHITE, border: `1px solid ${GOLD_BORDER}`, borderRadius: '16px', padding: '24px 28px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ width: '52px', height: '52px', borderRadius: '50%', background: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', flexShrink: 0, boxShadow: `0 4px 14px rgba(201,168,76,0.3)` }}>
              {userData?.full_name?.charAt(0)?.toUpperCase() || '🌟'}
            </div>
            <div style={{ flex: 1 }}>
              <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '22px', color: CHARCOAL, fontWeight: '600', lineHeight: 1, marginBottom: '4px' }}>
                Welcome back{userData?.full_name ? `, ${userData.full_name.split(' ')[0]}` : ''}
              </h1>
              <p style={{ fontSize: '13px', color: MUTED }}>{profiles.length} pageant{profiles.length !== 1 ? 's' : ''} in your history</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '28px', color: GOLD, fontWeight: '700', lineHeight: 1 }}>{profiles.length}</div>
              <div style={{ fontSize: '11px', color: MUTED }}>competitions</div>
            </div>
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', borderBottom: `2px solid ${GOLD_BORDER}`, marginBottom: '24px' }}>
            {tabs.map(tab => (
              <button key={tab.id} className="tab-btn" onClick={() => { setActiveTab(tab.id); setSelectedProfile(null); }} style={{ padding: '10px 20px', background: 'none', border: 'none', borderBottom: activeTab === tab.id ? `2px solid ${GOLD}` : '2px solid transparent', marginBottom: '-2px', fontSize: '13px', color: activeTab === tab.id ? GOLD : MUTED, cursor: 'pointer', fontFamily: "'Jost', sans-serif", fontWeight: activeTab === tab.id ? '600' : '400', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                {tab.label}
                {tab.count !== undefined && <span style={{ background: GOLD_PALE, color: GOLD, borderRadius: '20px', padding: '1px 7px', fontSize: '11px', fontWeight: '600' }}>{tab.count}</span>}
              </button>
            ))}
          </div>

          {/* MY PAGEANTS TAB */}
          {activeTab === 'history' && !selectedProfile && (
            <div>
              {profiles.length === 0 ? (
                <div style={{ background: WHITE, border: `1px solid ${GOLD_BORDER}`, borderRadius: '14px', padding: '48px', textAlign: 'center' }}>
                  <div style={{ fontSize: '40px', marginBottom: '12px' }}>🌟</div>
                  <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '22px', color: CHARCOAL, marginBottom: '8px' }}>No Pageants Yet</h3>
                  <p style={{ fontSize: '13px', color: MUTED, lineHeight: '1.7', maxWidth: '300px', margin: '0 auto' }}>Your pageant history will appear here once a director adds you to a competition. Ask your director for the pageant join code.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  {profiles.map(profile => {
                    const pageant = profile.pageants;
                    const isReleased = pageant?.status === 'closed';
                    const statusColors = {
                      draft:   { bg: '#F3F4F6', text: '#6B7280' },
                      active:  { bg: '#E8F5E9', text: '#2E7D32' },
                      scoring: { bg: '#FFF8E1', text: '#F57F17' },
                      closed:  { bg: '#E8F0FE', text: '#1A56DB' },
                    };
                    const sc = statusColors[pageant?.status] || statusColors.draft;
                    return (
                      <div key={profile.id} className="pageant-card" onClick={() => setSelectedProfile(profile)} style={{ background: WHITE, border: `1px solid ${GOLD_BORDER}`, borderRadius: '14px', padding: '20px 24px', transition: 'all 0.2s' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                          <div>
                            <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '20px', color: CHARCOAL, fontWeight: '600', marginBottom: '4px' }}>{pageant?.name}</h3>
                            <p style={{ fontSize: '12px', color: MUTED }}>{pageant?.organizations?.name}</p>
                          </div>
                          <span style={{ background: sc.bg, color: sc.text, borderRadius: '20px', padding: '4px 12px', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', flexShrink: 0 }}>{pageant?.status}</span>
                        </div>
                        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                          {profile.contestant_number && <div style={{ fontSize: '12px', color: MUTED }}>🔢 #{profile.contestant_number}</div>}
                          {profile.local_title && <div style={{ fontSize: '12px', color: MUTED }}>👑 {profile.local_title}</div>}
                          {profile.platform && <div style={{ fontSize: '12px', color: MUTED }}>💬 {profile.platform}</div>}
                          {pageant?.event_date && <div style={{ fontSize: '12px', color: MUTED }}>📅 {new Date(pageant.event_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</div>}
                        </div>
                        {isReleased && (
                          <div style={{ marginTop: '12px', display: 'inline-flex', alignItems: 'center', gap: '6px', background: GOLD_PALE, border: `1px solid ${GOLD_BORDER}`, borderRadius: '20px', padding: '5px 12px', fontSize: '12px', color: GOLD, fontWeight: '500' }}>
                            ✨ Results available — tap to view
                          </div>
                        )}
                        {!isReleased && pageant?.status === 'scoring' && (
                          <div style={{ marginTop: '12px', display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#FFF8E1', border: '1px solid #FFE082', borderRadius: '20px', padding: '5px 12px', fontSize: '12px', color: '#F57F17' }}>
                            ⏳ Scoring in progress...
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* SCORE DETAIL VIEW */}
          {activeTab === 'history' && selectedProfile && (
            <div>
              <button onClick={() => setSelectedProfile(null)} style={{ background: 'none', border: `1px solid ${GOLD_BORDER}`, borderRadius: '8px', padding: '8px 14px', cursor: 'pointer', fontSize: '13px', color: MUTED, fontFamily: "'Jost', sans-serif", marginBottom: '20px' }}>← Back</button>
              <ScoreReveal profile={selectedProfile} pageant={selectedProfile.pageants} />
            </div>
          )}

          {/* PROFILE TAB */}
          {activeTab === 'profile' && (
            <div>
              <div style={{ background: WHITE, border: `1px solid ${GOLD_BORDER}`, borderRadius: '14px', padding: '24px 28px', marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '22px', color: CHARCOAL }}>My Profile</h2>
                  {!editMode ? (
                    <GoldButton small outline onClick={() => setEditMode(true)}>Edit Profile</GoldButton>
                  ) : (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <GoldButton small outline onClick={() => { setEditMode(false); setSaved(false); }}>Cancel</GoldButton>
                      <GoldButton small onClick={saveProfile} disabled={saving}>{saving ? 'Saving...' : saved ? '✓ Saved!' : 'Save'}</GoldButton>
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ fontSize: '11px', color: MUTED, letterSpacing: '0.1em', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>Full Name</label>
                  {editMode ? <input className="profile-input" value={fullName} onChange={e => setFullName(e.target.value)} placeholder="Your full name" /> : <div style={{ fontSize: '15px', color: CHARCOAL, padding: '4px 0' }}>{fullName || '—'}</div>}
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ fontSize: '11px', color: MUTED, letterSpacing: '0.1em', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>Email</label>
                  <div style={{ fontSize: '14px', color: MUTED, padding: '4px 0' }}>{user.email}</div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ fontSize: '11px', color: MUTED, letterSpacing: '0.1em', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>Phone</label>
                  {editMode ? <input className="profile-input" value={phone} onChange={e => setPhone(e.target.value)} placeholder="Your phone number" /> : <div style={{ fontSize: '14px', color: CHARCOAL, padding: '4px 0' }}>{phone || '—'}</div>}
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ fontSize: '11px', color: MUTED, letterSpacing: '0.1em', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>Bio</label>
                  {editMode ? <textarea className="profile-input" value={bio} onChange={e => setBio(e.target.value)} placeholder="Tell us about yourself..." style={{ minHeight: '100px', resize: 'vertical' }} /> : <div style={{ fontSize: '14px', color: CHARCOAL, padding: '4px 0', lineHeight: '1.7' }}>{bio || '—'}</div>}
                </div>
              </div>

              {/* Pageant stats */}
              <div style={{ background: WHITE, border: `1px solid ${GOLD_BORDER}`, borderRadius: '14px', padding: '24px 28px' }}>
                <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '18px', color: CHARCOAL, marginBottom: '16px' }}>Competition Stats</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px' }}>
                  {[
                    { label: 'Competitions', value: profiles.length },
                    { label: 'Results Released', value: profiles.filter(p => p.pageants?.status === 'closed').length },
                    { label: 'Active Now', value: profiles.filter(p => ['active', 'scoring'].includes(p.pageants?.status)).length },
                  ].map(stat => (
                    <div key={stat.label} style={{ background: GOLD_PALE, border: `1px solid ${GOLD_BORDER}`, borderRadius: '10px', padding: '14px', textAlign: 'center' }}>
                      <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '28px', color: GOLD, fontWeight: '700', lineHeight: 1, marginBottom: '4px' }}>{stat.value}</div>
                      <div style={{ fontSize: '11px', color: MUTED }}>{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </>
  );
}